/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { ReactNode } from 'react'
import { font, paragraphSmall, weightSemiBold } from 'src/components/css'

interface EmptyStateProps {
  /** Icon rendered above the title — typically an @ant-design/icons element. */
  icon?: ReactNode
  /** Short label — rendered in Title Case via the shared helper. */
  title: string
  /** Optional supporting sentence below the title. */
  subtitle?: string
  /** Optional inline link appended to the subtitle. */
  linkText?: string
  onPressLink?: () => void
  /** Minimum height of the centered block. Defaults to 320px. */
  minHeight?: string
  className?: string
}

/**
 * Shared centered empty state: muted icon, Title Case heading, optional
 * subtitle with an optional inline link. Mirrors the mobile
 * `components/v2/EmptyState` so both platforms read the same.
 */
const EmptyState = ({
  icon,
  title,
  subtitle,
  linkText,
  onPressLink,
  minHeight = '320px',
  className,
}: EmptyStateProps) => {
  const showLink = Boolean(linkText && onPressLink)

  return (
    <div css={styles.container(minHeight)} className={className}>
      {icon && <div css={styles.icon}>{icon}</div>}
      <div css={styles.title}>{capitalizeWords(title)}</div>
      {(subtitle || showLink) && (
        <div css={styles.subtitle}>
          {subtitle}
          {showLink && (
            <>
              {subtitle ? <br /> : null}
              <button type='button' css={styles.link} onClick={onPressLink}>
                {linkText}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const styles = {
  container: (minHeight: string) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: ${minHeight};
    padding: 40px 24px;
    text-align: center;
  `,
  icon: css`
    display: flex;
    font-size: 40px;
    line-height: 1;
    color: ${colors.NEUTRAL_50};
    margin-bottom: 8px;
  `,
  // `font.heading` not `font.title`: on Canlan `title` forces uppercase, which
  // would defeat the Title Case and diverge from the mobile empty state.
  title: css`
    ${font.heading}
    ${weightSemiBold}
    font-size: 20px;
    line-height: 24px;
    color: ${colors.NEUTRAL_0};
  `,
  subtitle: css`
    ${font.body}
    ${paragraphSmall}
    color: ${colors.NEUTRAL_100};
    margin-top: 6px;
    /* Honours explicit \n in the copy, which HTML would otherwise collapse to a
       space — mobile's Text renders them natively, so this keeps both in sync. */
    white-space: pre-line;
  `,
  link: css`
    ${font.body}
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${colors.PRIMARY};
    font-size: inherit;
    line-height: inherit;

    &:hover {
      opacity: 0.8;
    }
  `,
}

export default EmptyState
